/**
 * Photo Loader Module
 * Handles loading photos with GPS EXIF metadata and converting to GeoJSON points
 */

import JSZip from 'jszip';

/**
 * Read EXIF data from image file
 * Uses exifr library for parsing EXIF data
 * @param {File} file - Image file
 * @returns {Promise<Object>} EXIF data
 */
async function readEXIF(file) {
    try {
        // Dynamically import exifr if available, otherwise use fallback
        // For now, we'll use a simple EXIF reader
        const arrayBuffer = await file.arrayBuffer();
        const dataView = new DataView(arrayBuffer);

        // Check for JPEG signature
        if (dataView.getUint8(0) !== 0xFF || dataView.getUint8(1) !== 0xD8) {
            throw new Error('Not a valid JPEG file');
        }

        // Simple EXIF extraction - look for GPS data
        const exifData = await extractGPSData(dataView);
        return exifData;
    } catch (error) {
        console.error('Error reading EXIF:', error);
        throw error;
    }
}

/**
 * Extract GPS data from JPEG DataView
 * @param {DataView} dataView - JPEG data
 * @returns {Object} GPS coordinates and metadata
 */
async function extractGPSData(dataView) {
    // This is a simplified EXIF parser focusing on GPS data
    // For production, consider using a library like exifr

    let offset = 2; // Skip JPEG signature
    const length = dataView.byteLength;

    // Look for APP1 marker (0xFFE1) which contains EXIF
    while (offset < length) {
        if (dataView.getUint8(offset) !== 0xFF) {
            break;
        }

        const marker = dataView.getUint8(offset + 1);
        const size = dataView.getUint16(offset + 2, false);

        if (marker === 0xE1) { // APP1 marker (EXIF)
            // Check for EXIF identifier
            const exifID = String.fromCharCode(
                dataView.getUint8(offset + 4),
                dataView.getUint8(offset + 5),
                dataView.getUint8(offset + 6),
                dataView.getUint8(offset + 7)
            );

            if (exifID === 'Exif') {
                // Found EXIF data - parse it
                return parseEXIFBlock(dataView, offset + 10);
            }
        }

        offset += 2 + size;
    }

    throw new Error('No EXIF data found in image');
}

/**
 * Parse EXIF block for GPS data
 * @param {DataView} dataView - Data view
 * @param {number} offset - Offset to TIFF header
 * @returns {Object} GPS data
 */
function parseEXIFBlock(dataView, offset) {
    // Read TIFF header
    const tiffOffset = offset;
    const byteOrder = dataView.getUint16(offset, false);
    const littleEndian = byteOrder === 0x4949; // 'II' = little endian

    // We'll use a simplified approach and try to use the browser's built-in capabilities
    // For now, return a placeholder that indicates we need to use a library
    throw new Error('GPS EXIF parsing requires exifr library - loading...');
}

/**
 * Load exifr library dynamically
 */
async function loadExifr() {
    // Check if exifr is already loaded
    if (window.exifr) {
        return window.exifr;
    }

    // Load exifr from CDN
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/exifr@latest/dist/lite.umd.js';
        script.onload = () => resolve(window.exifr);
        script.onerror = () => reject(new Error('Failed to load exifr library'));
        document.head.appendChild(script);
    });
}

/**
 * Convert GPS coordinates from EXIF format to decimal degrees
 * @param {Array} coord - GPS coordinate in EXIF format [degrees, minutes, seconds]
 * @param {string} ref - Reference (N/S for lat, E/W for lon)
 * @returns {number} Decimal degrees
 */
function convertGPSCoordinate(coord, ref) {
    if (!coord || coord.length < 3) return null;

    const degrees = coord[0];
    const minutes = coord[1];
    const seconds = coord[2];

    let decimal = degrees + (minutes / 60) + (seconds / 3600);

    // Apply direction
    if (ref === 'S' || ref === 'W') {
        decimal = -decimal;
    }

    return decimal;
}

/**
 * Create thumbnail from image
 * @param {File} file - Image file
 * @param {number} maxSize - Maximum dimension
 * @returns {Promise<string>} Data URL of thumbnail
 */
async function createThumbnail(file, maxSize = 200) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Calculate new dimensions
                if (width > height) {
                    if (width > maxSize) {
                        height = height * (maxSize / width);
                        width = maxSize;
                    }
                } else {
                    if (height > maxSize) {
                        width = width * (maxSize / height);
                        height = maxSize;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.onerror = reject;
            img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/**
 * Load photo and extract GPS coordinates
 * @param {File} file - Photo file
 * @param {Function} progressCallback - Progress callback
 * @returns {Object} Result with geojson, fileName, and featureCount
 */
export async function loadPhoto(file, progressCallback) {
    try {
        progressCallback(10, 'Reading photo...');

        // Load exifr library
        let exifr;
        try {
            exifr = await loadExifr();
        } catch (error) {
            throw new Error('Failed to load EXIF reader library. Please check your internet connection.');
        }

        progressCallback(30, 'Extracting GPS data...');

        // Read EXIF data
        const exifData = await exifr.gps(file);

        if (!exifData || (!exifData.latitude && !exifData.longitude)) {
            throw new Error('No GPS data found in photo. Make sure the photo was taken with location services enabled.');
        }

        const lat = exifData.latitude;
        const lon = exifData.longitude;

        // Validate coordinates
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
            throw new Error(`Invalid GPS coordinates: ${lat}, ${lon}`);
        }

        progressCallback(60, 'Creating thumbnail...');

        // Create thumbnail
        const thumbnail = await createThumbnail(file);

        progressCallback(80, 'Processing metadata...');

        // Get additional EXIF data
        const fullExif = await exifr.parse(file, {
            tiff: true,
            exif: true,
            gps: true,
            interop: false,
            ifd1: false
        });

        // Build properties
        const properties = {
            name: file.name,
            photo_file: file.name,
            latitude: lat,
            longitude: lon,
            thumbnail: thumbnail,
            __featureIndex: 0
        };

        // Add additional metadata if available
        if (fullExif) {
            if (fullExif.Make) properties.camera_make = fullExif.Make;
            if (fullExif.Model) properties.camera_model = fullExif.Model;
            if (fullExif.DateTime) properties.date_time = fullExif.DateTime;
            if (fullExif.DateTimeOriginal) properties.date_taken = fullExif.DateTimeOriginal;
            if (exifData.altitude) properties.altitude = exifData.altitude;
        }

        // Create GeoJSON feature
        const feature = {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [lon, lat]
            },
            properties
        };

        progressCallback(95, 'Processing complete...');

        return {
            geojson: {
                type: 'FeatureCollection',
                features: [feature]
            },
            fileName: file.name.replace(/\.(jpg|jpeg|png|heic)$/i, ''),
            featureCount: 1,
            geometryTypes: ['Point']
        };

    } catch (error) {
        console.error('Error loading photo:', error);
        throw error;
    }
}

/**
 * Check if file is an image
 * @param {File} file - File to check
 * @returns {boolean} True if image
 */
export function isImageFile(file) {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif'];
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];

    const hasImageType = imageTypes.includes(file.type.toLowerCase());
    const hasImageExtension = imageExtensions.some(ext =>
        file.name.toLowerCase().endsWith(ext)
    );

    return hasImageType || hasImageExtension;
}

/**
 * Load multiple photos and combine into a single GeoJSON layer
 * @param {Array<File>} files - Array of photo files
 * @param {Function} progressCallback - Progress callback
 * @returns {Object} Result with combined geojson, layer name, and feature count
 */
export async function loadPhotos(files, progressCallback) {
    try {
        const features = [];
        const errors = [];
        let successCount = 0;

        progressCallback(5, 'Loading EXIF reader...');

        // Load exifr library once
        let exifr;
        try {
            exifr = await loadExifr();
        } catch (error) {
            throw new Error('Failed to load EXIF reader library. Please check your internet connection.');
        }

        progressCallback(10, `Processing ${files.length} photos...`);

        // Process each photo
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const progress = 10 + (i / files.length) * 80;

            try {
                progressCallback(progress, `Processing ${file.name}...`);

                // Read EXIF data
                const exifData = await exifr.gps(file);

                if (!exifData || (!exifData.latitude && !exifData.longitude)) {
                    console.warn(`No GPS data in ${file.name}`);
                    errors.push({ file: file.name, error: 'No GPS data' });
                    continue;
                }

                const lat = exifData.latitude;
                const lon = exifData.longitude;

                // Validate coordinates
                if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
                    console.warn(`Invalid coordinates in ${file.name}: ${lat}, ${lon}`);
                    errors.push({ file: file.name, error: 'Invalid coordinates' });
                    continue;
                }

                // Create thumbnail
                const thumbnail = await createThumbnail(file);

                // Get additional EXIF data
                const fullExif = await exifr.parse(file, {
                    tiff: true,
                    exif: true,
                    gps: true,
                    interop: false,
                    ifd1: false
                });

                // Build properties
                const properties = {
                    name: file.name,
                    photo_file: file.name,
                    latitude: lat,
                    longitude: lon,
                    thumbnail: thumbnail,
                    __featureIndex: successCount
                };

                // Add additional metadata if available
                if (fullExif) {
                    if (fullExif.Make) properties.camera_make = fullExif.Make;
                    if (fullExif.Model) properties.camera_model = fullExif.Model;
                    if (fullExif.DateTime) properties.date_time = fullExif.DateTime;
                    if (fullExif.DateTimeOriginal) properties.date_taken = fullExif.DateTimeOriginal;
                    if (exifData.altitude) properties.altitude = exifData.altitude;
                }

                // Create GeoJSON feature
                const feature = {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [lon, lat]
                    },
                    properties
                };

                features.push(feature);
                successCount++;

            } catch (error) {
                console.error(`Error processing ${file.name}:`, error);
                errors.push({ file: file.name, error: error.message });
            }
        }

        progressCallback(95, 'Finalizing layer...');

        if (features.length === 0) {
            const errorMsg = errors.length > 0
                ? `No valid GPS data found in any photos. Errors:\n${errors.map(e => `${e.file}: ${e.error}`).join('\n')}`
                : 'No photos could be processed.';
            throw new Error(errorMsg);
        }

        // Show warnings if some photos failed
        if (errors.length > 0) {
            console.warn(`${errors.length} photos could not be processed:`, errors);
            // Could show notification to user
        }

        return {
            geojson: {
                type: 'FeatureCollection',
                features
            },
            fileName: files.length > 1 ? `Photos (${features.length})` : files[0].name.replace(/\.(jpg|jpeg|png|heic)$/i, ''),
            featureCount: features.length,
            geometryTypes: ['Point'],
            errors: errors.length > 0 ? errors : null
        };

    } catch (error) {
        console.error('Error loading photos:', error);
        throw error;
    }
}

/**
 * Extract photos from a zip file
 * @param {File} zipFile - Zip file containing photos
 * @returns {Promise<Array<File>>} Array of photo files
 */
export async function extractPhotosFromZip(zipFile) {
    try {
        const zip = await JSZip.loadAsync(zipFile);
        const photoFiles = [];
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];

        // Iterate through all files in the zip
        for (const [filename, zipEntry] of Object.entries(zip.files)) {
            // Skip directories and hidden files
            if (zipEntry.dir || filename.startsWith('__MACOSX') || filename.startsWith('.')) {
                continue;
            }

            // Check if it's an image file
            const filenameLower = filename.toLowerCase();
            const isImage = imageExtensions.some(ext => filenameLower.endsWith(ext));

            if (isImage) {
                // Extract file as blob
                const blob = await zipEntry.async('blob');

                // Get just the filename without path
                const name = filename.split('/').pop();

                // Create File object from blob
                const file = new File([blob], name, {
                    type: blob.type || 'image/jpeg'
                });

                photoFiles.push(file);
            }
        }

        return photoFiles;
    } catch (error) {
        console.error('Error extracting photos from zip:', error);
        throw new Error(`Failed to extract photos from zip: ${error.message}`);
    }
}
